import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Eye,
  EyeOff,
  User,
  Building,
  Lock,
  Sparkles,
  Brain,
  TrendingUp,
  Loader2,
} from 'lucide-react';
import { FcGoogle } from 'react-icons/fc';
import { motion } from 'framer-motion';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuthContext } from '../hooks/useAuth';
import { usePageTitle } from '../hooks/usePageTitle';
import logger from '../utils/logger';
import { cn } from '@/lib/utils';

// プラン定義
interface PlanOption {
  id: 'light' | 'standard' | 'premium';
  name: string;
  price: string;
  description: string;
  features: string[];
  popular?: boolean;
}

const PLAN_OPTIONS: PlanOption[] = [
  {
    id: 'light',
    name: 'ライト',
    price: '¥19,800',
    description: '小規模サロン向け',
    features: ['基本予約管理', '顧客管理（100名まで）', 'メール通知', 'モバイル対応'],
  },
  {
    id: 'standard',
    name: 'スタンダード',
    price: '¥39,800',
    description: '中規模サロン向け',
    features: ['全ライト機能', '顧客管理（500名まで）', 'スタッフ管理', 'レポート機能', 'API連携'],
    popular: true,
  },
  {
    id: 'premium',
    name: 'プレミアム',
    price: '¥79,800',
    description: 'エンタープライズ向け',
    features: ['全スタンダード機能', '無制限顧客管理', 'マルチ店舗', 'AI分析', '優先サポート'],
  },
];

// 業界タイプ定義
const SALON_BUSINESS_TYPES = [
  { value: 'beauty_salon', label: '美容室' },
  { value: 'relaxation_salon', label: 'リラクゼーションサロン' },
  { value: 'nail_salon', label: 'ネイルサロン' },
  { value: 'eyelash_salon', label: 'アイラッシュサロン' },
  { value: 'esthetic_salon', label: 'エステサロン' },
  { value: 'head_spa', label: 'ヘッドスパ' },
  { value: 'barbershop', label: '理容室' },
  { value: 'other', label: 'その他' },
];

// フォームデータ型定義
interface RegisterFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  salonName: string;
  businessType: string;
  selectedPlan: 'light' | 'standard' | 'premium';
  termsAccepted: boolean;
}

// パスワード強度評価
interface PasswordStrength {
  score: number;
  label: string;
  color: 'error' | 'warning' | 'info' | 'success';
  requirements: {
    length: boolean;
    uppercase: boolean;
    lowercase: boolean;
    number: boolean;
    symbol: boolean;
  };
}

/**
 * Register Page - World-Class UI Implementation
 *
 * Features:
 * - 2-column layout (form + visual content)
 * - Multi-section form with Cards
 * - Password strength indicator
 * - Plan selection cards
 * - shadcn/ui Design System
 * - Responsive design
 * - WCAG 2.1 AA compliant
 *
 * @returns Register page component
 */
const Register: React.FC = () => {
  usePageTitle('新規登録');
  const navigate = useNavigate();
  const { registerWithEmailVerification, googleLogin, loading, error, clearError } = useAuthContext();

  // フォーム状態
  const [formData, setFormData] = useState<RegisterFormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    salonName: '',
    businessType: '',
    selectedPlan: 'standard',
    termsAccepted: false,
  });

  // UI状態
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [passwordFocused, setPasswordFocused] = useState(false);

  // パスワード強度評価
  const evaluatePasswordStrength = (password: string): PasswordStrength => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      symbol: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    const score = Object.values(requirements).filter(Boolean).length;

    let label: string;
    let color: 'error' | 'warning' | 'info' | 'success';

    if (score <= 2) {
      label = '弱い';
      color = 'error';
    } else if (score <= 3) {
      label = '普通';
      color = 'warning';
    } else if (score <= 4) {
      label = '強い';
      color = 'info';
    } else {
      label = '非常に強い';
      color = 'success';
    }

    return { score, label, color, requirements };
  };

  const passwordStrength = evaluatePasswordStrength(formData.password);

  // フォーム入力ハンドラー
  const handleInputChange = (field: keyof RegisterFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: string } }
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // エラーをクリア
    if (localError || error) {
      setLocalError('');
      clearError();
    }
  };

  const handleCheckboxChange = (checked: boolean) => {
    setFormData(prev => ({ ...prev, termsAccepted: checked }));
  };

  // バリデーション
  const validateForm = (): string | null => {
    if (!formData.firstName.trim()) {
      return '姓を入力してください';
    }
    if (!formData.lastName.trim()) {
      return '名を入力してください';
    }
    if (!formData.email.trim()) {
      return 'メールアドレスを入力してください';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      return '有効なメールアドレスを入力してください';
    }
    if (!formData.salonName.trim()) {
      return 'サロン名を入力してください';
    }
    if (!formData.businessType) {
      return '業界タイプを選択してください';
    }
    if (!formData.password) {
      return 'パスワードを入力してください';
    }
    if (passwordStrength.score < 4) {
      return 'より強力なパスワードを設定してください';
    }
    if (formData.password !== formData.confirmPassword) {
      return 'パスワードが一致しません';
    }
    if (!formData.termsAccepted) {
      return '利用規約とプライバシーポリシーに同意してください';
    }
    
    return null;
  };

  // 登録処理
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    // エラーをクリア
    clearError();
    setLocalError('');

    // バリデーション
    const validationError = validateForm();
    if (validationError) {
      setLocalError(validationError);
      return;
    }

    try {
      logger.log('📝 マルチテナント新規登録開始:', formData.email);

      // 新規登録実行（マルチテナント対応）
      const response = await registerWithEmailVerification({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        password: formData.password,
        organizationName: formData.salonName.trim(),
        businessType: formData.businessType as any,
        selectedPlan: formData.selectedPlan,
      });

      if (response.success && response.verificationRequired) {
        logger.log('✅ メール認証付き新規登録成功:', formData.email);
        logger.log('📧 メール認証画面に遷移:', response.userId);

        // メール認証画面に遷移
        navigate(`/email-verification/${response.userId}`);
      } else {
        // 認証が不要な場合は直接ダッシュボードへ
        navigate('/dashboard');
      }
    } catch (err) {
      logger.error('❌ 新規登録エラー:', err);

      if (err instanceof Error) {
        setLocalError(err.message);
      } else {
        setLocalError('アカウント作成に失敗しました。しばらく後にお試しください。');
      }
    }
  };

  /**
   * Google登録ハンドラ
   * Google OAuthで新規登録を実行
   */
  const handleGoogleRegister = async () => {
    try {
      clearError();
      setLocalError('');

      logger.log('🚀 Google登録開始');
      await googleLogin();

      logger.log('✅ Google登録成功');
      navigate('/dashboard');
    } catch (err) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Google登録に失敗しました';

      logger.error('❌ Google登録エラー:', err);
      setLocalError(errorMessage);
    }
  };

  // 表示するエラー
  const displayError = localError || error;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 min-h-screen">
      {/* LEFT COLUMN: Registration Form */}
      <div className="flex flex-col justify-center p-4 md:p-8 bg-gray-50 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="max-w-[480px] mx-auto w-full">
            {/* SALONHUB Logo */}
            <img
              src="/assets/logos/salon-hub-logo.png"
              alt="SALON HUB"
              className="w-full max-w-[175px] h-auto mb-4 transition-transform duration-300 hover:scale-105"
            />

            {/* Title */}
            <h1 className="scroll-m-20 text-2xl font-bold tracking-tight text-gray-900 mb-6 leading-tight">
              アカウント作成
            </h1>

            <form onSubmit={handleRegister}>
              {/* 基本情報セクション */}
              <Card className="mb-3">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <User className="w-6 h-6 text-primary" />
                    <CardTitle className="text-lg font-semibold">基本情報</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex gap-2">
                    <div className="flex-1 space-y-2">
                      <Label htmlFor="firstName">姓</Label>
                      <Input
                        id="firstName"
                        required
                        value={formData.firstName}
                        onChange={handleInputChange('firstName')}
                        className="bg-white"
                      />
                    </div>
                    <div className="flex-1 space-y-2">
                      <Label htmlFor="lastName">名</Label>
                      <Input
                        id="lastName"
                        required
                        value={formData.lastName}
                        onChange={handleInputChange('lastName')}
                        className="bg-white"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">メールアドレス</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange('email')}
                      placeholder="example@salonhub.co.jp"
                      className={cn(
                        "bg-white",
                        displayError && displayError.includes('既に登録されています') && "border-red-500"
                      )}
                    />
                    {displayError && displayError.includes('既に登録されています') && (
                      <p className="text-sm text-red-500 whitespace-pre-line">{displayError}</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* サロン情報セクション */}
              <Card className="mb-3">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Building className="w-6 h-6 text-primary" />
                    <CardTitle className="text-lg font-semibold">サロン情報</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="salonName">サロン・店舗名</Label>
                    <Input
                      id="salonName"
                      required
                      value={formData.salonName}
                      onChange={handleInputChange('salonName')}
                      className="bg-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="businessType">業界タイプ</Label>
                    <Select
                      value={formData.businessType}
                      onValueChange={(value) => handleInputChange('businessType')({ target: { value } })}
                    >
                      <SelectTrigger id="businessType" className="bg-white">
                        <SelectValue placeholder="選択してください" />
                      </SelectTrigger>
                      <SelectContent>
                        {SALON_BUSINESS_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* パスワード設定セクション */}
              <Card className="mb-3">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Lock className="w-6 h-6 text-primary" />
                    <CardTitle className="text-lg font-semibold">パスワード設定</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="password">パスワード</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={formData.password}
                        onChange={handleInputChange('password')}
                        onFocus={() => setPasswordFocused(true)}
                        onBlur={() => setPasswordFocused(false)}
                        className="bg-white pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label="パスワード表示切替"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* パスワード強度インジケーター */}
                  {(formData.password || passwordFocused) && (
                    <div className="mb-2">
                      <div className="flex items-center mb-1">
                        <p className="text-sm mr-1">パスワード強度:</p>
                        <p className={cn(
                          "text-sm font-semibold",
                          passwordStrength.color === 'error' && "text-red-500",
                          passwordStrength.color === 'warning' && "text-orange-500",
                          passwordStrength.color === 'info' && "text-blue-500",
                          passwordStrength.color === 'success' && "text-green-500"
                        )}>
                          {passwordStrength.label}
                        </p>
                      </div>
                      <Progress
                        value={(passwordStrength.score / 5) * 100}
                        className={cn(
                          "h-2 mb-1",
                          passwordStrength.color === 'error' && "[&>div]:bg-red-500",
                          passwordStrength.color === 'warning' && "[&>div]:bg-orange-500",
                          passwordStrength.color === 'info' && "[&>div]:bg-blue-500",
                          passwordStrength.color === 'success' && "[&>div]:bg-green-500"
                        )}
                      />
                      <div className="text-xs text-gray-600">
                        {Object.entries({
                          length: '8文字以上',
                          uppercase: '大文字',
                          lowercase: '小文字',
                          number: '数字',
                          symbol: '記号',
                        }).map(([key, label]) => (
                          <p
                            key={key}
                            className={cn(
                              "text-xs",
                              passwordStrength.requirements[key as keyof typeof passwordStrength.requirements]
                                ? "text-green-500"
                                : "text-gray-500"
                            )}
                          >
                            {passwordStrength.requirements[key as keyof typeof passwordStrength.requirements] ? '✓' : '×'} {label}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">パスワード確認</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        required
                        value={formData.confirmPassword}
                        onChange={handleInputChange('confirmPassword')}
                        className={cn(
                          "bg-white pr-10",
                          formData.password !== formData.confirmPassword && formData.confirmPassword !== '' && "border-red-500"
                        )}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label="パスワード表示切替"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </Button>
                    </div>
                    {formData.password !== formData.confirmPassword && formData.confirmPassword !== '' && (
                      <p className="text-sm text-red-500">パスワードが一致しません</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* プラン選択セクション */}
              <div className="mb-3">
                <p className="text-sm font-medium text-gray-700 mb-2">プラン選択</p>
                <div className="grid grid-cols-3 gap-2">
                  {PLAN_OPTIONS.map((plan) => (
                    <div
                      key={plan.id}
                      onClick={() => setFormData(prev => ({ ...prev, selectedPlan: plan.id }))}
                      className={cn(
                        "relative p-2 rounded-md border-2 cursor-pointer transition-all",
                        formData.selectedPlan === plan.id
                          ? "border-primary bg-primary/5"
                          : "border-gray-200 bg-white hover:border-primary/40 hover:shadow-sm"
                      )}
                    >
                      {plan.popular && (
                        <div className="absolute -top-1 -right-1 bg-primary text-white px-1 py-0.5 rounded-sm text-[0.6rem] font-semibold">
                          人気
                        </div>
                      )}
                      <p className="font-bold text-sm text-gray-900 mb-0.5">{plan.name}</p>
                      <p className="font-bold text-lg text-primary mb-0.5">
                        {plan.price}
                        <span className="text-xs text-gray-600">/月</span>
                      </p>
                      <p className="text-xs text-gray-600">{plan.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* 利用規約同意 */}
              <div className="flex items-center space-x-2 mb-3">
                <Checkbox
                  id="termsAccepted"
                  checked={formData.termsAccepted}
                  onCheckedChange={handleCheckboxChange}
                />
                <label
                  htmlFor="termsAccepted"
                  className="text-sm text-gray-600 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  <a
                    href="https://salonhub.co.jp/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary/80 font-medium transition-colors"
                  >
                    利用規約
                  </a>
                  と
                  <a
                    href="https://salonhub.co.jp/policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary/80 font-medium transition-colors"
                  >
                    プライバシーポリシー
                  </a>
                  に同意する
                </label>
              </div>

              {/* エラーメッセージ表示 */}
              {displayError && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600 font-medium whitespace-pre-line">{displayError}</p>
                </div>
              )}

              {/* 登録ボタン */}
              <Button
                type="submit"
                className="w-full min-h-[56px] py-2.5 mb-3"
                disabled={loading || !formData.termsAccepted}
              >
                {loading ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  'アカウントを作成'
                )}
              </Button>

              {/* Divider */}
              <div className="relative mb-3">
                <Separator />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-gray-50 px-2 text-sm text-gray-500">
                  または
                </span>
              </div>

              {/* Google登録ボタン */}
              <Button
                type="button"
                variant="outline"
                className="w-full min-h-[56px] py-2.5 bg-white mb-4"
                onClick={handleGoogleRegister}
                disabled={loading}
              >
                <FcGoogle size={24} className="mr-2" />
                Googleで登録
              </Button>

              {/* ログイン画面リンク */}
              <p className="text-sm text-gray-600">
                すでにアカウントをお持ちですか？{' '}
                <RouterLink
                  to="/login"
                  className="font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  ログイン
                </RouterLink>
              </p>
            </form>
          </div>
        </motion.div>
      </div>

      {/* RIGHT COLUMN: Visual Content */}
      <div className="hidden md:flex flex-col justify-center items-center p-8 bg-gradient-to-br from-primary via-primary/90 to-primary/80 text-white sticky top-0 h-screen overflow-hidden">
        <div className="absolute top-[-50%] right-[-50%] w-[200%] h-[200%] bg-[radial-gradient(circle,rgba(255,255,255,0.1)_0%,transparent_70%)] animate-[pulse_8s_ease-in-out_infinite]" />

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          className="z-10 max-w-[500px] w-full"
        >
          {/* Welcome Message */}
          <h2 className="scroll-m-20 text-3xl md:text-4xl font-bold tracking-tight mb-3 text-center leading-[1.3] [text-shadow:0_2px_4px_rgba(0,0,0,0.1)]">
            データで見えるサロン経営
          </h2>

          {/* Divider */}
          <Separator className="my-4 bg-white/30" />

          {/* Features */}
          <div className="mt-6">
            {/* Feature 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="mb-4">
                <h5 className="font-semibold text-xl mb-1 flex items-center gap-2">
                  <Sparkles className="w-7 h-7" />
                  3日で売上UP施策を開始
                </h5>
                <p className="text-base opacity-95 leading-relaxed">
                  設定不要、ヒアリング後すぐに実行代行スタート
                </p>
              </div>
            </motion.div>

            {/* Feature 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div className="mb-4">
                <h5 className="font-semibold text-xl mb-1 flex items-center gap-2">
                  <Brain className="w-7 h-7" />
                  月20時間があなたの時間に
                </h5>
                <p className="text-base opacity-95 leading-relaxed">
                  顧客フォロー・SNS投稿・分析レポートを完全代行
                </p>
              </div>
            </motion.div>

            {/* Feature 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <div>
                <h5 className="font-semibold text-xl mb-1 flex items-center gap-2">
                  <TrendingUp className="w-7 h-7" />
                  平均15%の売上向上を実現
                </h5>
                <p className="text-base opacity-95 leading-relaxed">
                  導入店舗の平均実績データに基づく成果
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Register;
